import type { Helpers, Method } from './resources/internal.js';
import { createAuthResource } from './resources/auth.js';
import { createUsersResource } from './resources/users.js';
import {
  createCategoriasResource,
  createClientesResource,
  createSucursalesResource,
} from './resources/catalog.js';
import { createGuardiasResource, createHorasResource } from './resources/hr.js';
import { createUploadsResource } from './resources/uploads.js';
import {
  createCajasResource,
  createFinanzasResource,
  createTransaccionesResource,
} from './resources/finanzas.js';
import { createBancoResource } from './resources/banco.js';
import { createEfectivoResource } from './resources/efectivo.js';
import { createPagosResource } from './resources/pagos.js';
import { createPlanillaResource } from './resources/planilla.js';
import { createFlotaResource } from './resources/flota.js';
import { createInventarioResource } from './resources/inventario.js';
import { createLicenciasResource } from './resources/licencias.js';
import { createSuscripcionesResource } from './resources/suscripciones.js';
import { createAdminResource } from './resources/admin.js';
import { createReportesResource } from './resources/reportes.js';
import { createRecaudacionResource } from './resources/recaudacion.js';
import { createRecibosResource } from './resources/recibos.js';
import { createFacturasResource } from './resources/facturas.js';
import {
  createContactosResource,
  createPreReservasResource,
  createPresupuestosResource,
} from './resources/presupuestos.js';

export type { ListResult } from './resources/internal.js';

export interface ApiClientOptions {
  baseUrl: string;
  getCsrfToken?: () => string | null | undefined;
  getRequestId?: () => string | undefined;
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    // body de erro do backend (ex.: { error, details: zod.flatten() }) — preservado
    // para que o FE possa exibir erros de validação por campo (details.fieldErrors).
    public details?: unknown,
  ) {
    super(code);
    this.name = 'ApiError';
  }
}

function toApiError(status: number, data: unknown): ApiError {
  const obj = data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
  const code = obj && 'error' in obj ? String(obj.error) : 'request_failed';
  return new ApiError(status, code, obj?.details);
}

export function createApiClient(options: ApiClientOptions) {
  const { baseUrl, getCsrfToken, getRequestId, timeoutMs = 30000 } = options;
  const doFetch = options.fetchImpl ?? fetch;
  const root = baseUrl.replace(/\/$/, '');

  async function request(method: Method, path: string, body?: unknown): Promise<unknown> {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (method !== 'GET') {
      const csrf = getCsrfToken?.();
      if (csrf) headers['X-CSRF-Token'] = csrf;
      const rid = getRequestId?.();
      if (rid) headers['X-Request-Id'] = rid;
    }
    // AbortController evita request pendurado indefinidamente (leak em SSR/edge).
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await doFetch(`${root}${path}`, {
        method,
        headers,
        credentials: 'include',
        signal: controller.signal,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
      const data: unknown = await res.json().catch(() => null);
      if (!res.ok) {
        throw toApiError(res.status, data);
      }
      return data;
    } finally {
      clearTimeout(timer);
    }
  }

  // Content-Type é definido pelo browser (inclui boundary do multipart); não setar manualmente.
  async function requestMultipart(path: string, form: FormData): Promise<unknown> {
    const headers: Record<string, string> = {};
    const csrf = getCsrfToken?.();
    if (csrf) headers['X-CSRF-Token'] = csrf;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await doFetch(`${root}${path}`, {
        method: 'POST',
        headers,
        credentials: 'include',
        signal: controller.signal,
        body: form,
      });
      const data: unknown = await res.json().catch(() => null);
      if (!res.ok) {
        throw toApiError(res.status, data);
      }
      return data;
    } finally {
      clearTimeout(timer);
    }
  }

  async function requestBlob(path: string): Promise<Blob> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await doFetch(`${root}${path}`, {
        method: 'GET',
        credentials: 'include',
        signal: controller.signal,
      });
      if (!res.ok) {
        const data: unknown = await res.json().catch(() => null);
        throw toApiError(res.status, data);
      }
      return await res.blob();
    } finally {
      clearTimeout(timer);
    }
  }

  const helpers: Helpers = { request, requestMultipart, requestBlob };

  return {
    auth: createAuthResource(helpers),
    users: createUsersResource(helpers),
    sucursales: createSucursalesResource(helpers),
    clientes: createClientesResource(helpers),
    categorias: createCategoriasResource(helpers),
    guardias: createGuardiasResource(helpers),
    uploads: createUploadsResource(helpers),
    horas: createHorasResource(helpers),
    cajas: createCajasResource(helpers),
    transacciones: createTransaccionesResource(helpers),
    finanzas: createFinanzasResource(helpers),
    banco: createBancoResource(helpers),
    efectivo: createEfectivoResource(helpers),
    pagos: createPagosResource(helpers),
    planilla: createPlanillaResource(helpers),
    flota: createFlotaResource(helpers),
    inventario: createInventarioResource(helpers),
    licencias: createLicenciasResource(helpers),
    suscripciones: createSuscripcionesResource(helpers),
    admin: createAdminResource(helpers),
    reportes: createReportesResource(helpers),
    recaudacion: createRecaudacionResource(helpers),
    recibos: createRecibosResource(helpers),
    facturas: createFacturasResource(helpers),
    presupuestos: createPresupuestosResource(helpers),
    contactos: createContactosResource(helpers),
    preReservas: createPreReservasResource(helpers),
    search: {
      async query(q: string): Promise<{
        guardias: { id: string; nombre: string; cedula: string | null }[];
        clientes: { id: string; nombre: string }[];
        facturas: { id: string; nroFactura: string | null; estado: string | null }[];
      }> {
        return helpers.request('GET', `/api/search?q=${encodeURIComponent(q)}`) as Promise<{
          guardias: { id: string; nombre: string; cedula: string | null }[];
          clientes: { id: string; nombre: string }[];
          facturas: { id: string; nroFactura: string | null; estado: string | null }[];
        }>;
      },
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
